import one360 from './one360';
import type { DriveUploadedFile, DriveUploadResponse, DriveFileContent } from '../../interfaces/drive';

interface One360FileResult {
  fileId: string;
  fileName: string;
  webViewLink?: string;
  folderId?: string;
  path?: string;
}

export interface UploadDestinationOptions {
  sharedDrive?: string;
  gdriveFolderId?: string;
}

async function uploadOneFile(
  file: File,
  dealName: string,
  directories: string[],
  destination: UploadDestinationOptions = {},
): Promise<One360FileResult> {
  const { sharedDrive, gdriveFolderId } = destination;
  const formData = new FormData();
  formData.append('file', file);
  // dealName is always sent (even when gdriveFolderId is set) so the backend can
  // self-heal by name-based folder resolution if the cached folder id is stale.
  formData.append('dealName', dealName);
  if (gdriveFolderId) formData.append('folderId', gdriveFolderId);
  directories.forEach(dir => formData.append('directories', dir));
  if (sharedDrive) formData.append('sharedDrive', sharedDrive);
  return one360.post<One360FileResult>('/api/v1/google/drive/upload', formData);
}

export async function uploadFilesAsync(
  files: File[],
  dealName: string,
  directories: string[] = ['Dokumente', 'Allgemeine Uploads'],
  destination: UploadDestinationOptions = {},
): Promise<DriveUploadResponse> {
  const uploaded: DriveUploadedFile[] = [];
  const errors: string[] = [];

  for (const file of files) {
    try {
      const result = await uploadOneFile(file, dealName, directories, destination);
      uploaded.push({
        fileId: result.fileId,
        fileName: result.fileName,
        webViewLink: result.webViewLink ?? `https://drive.google.com/file/d/${result.fileId}`,
      });
    } catch (err: any) {
      console.error('[Drive] uploadFiles fehlgeschlagen:', err);
      errors.push(`${file.name}: ${err.message}`);
    }
  }

  return {
    message: errors.length ? 'Einige Dateien konnten nicht hochgeladen werden.' : 'Upload erfolgreich.',
    status: errors.length === 0,
    errors,
    files: uploaded.length ? uploaded : null,
  };
}

export async function uploadSingleFileAsync(
  imageData: string,
  fileName: string,
  dealName: string,
  directories?: string[],
  destination: UploadDestinationOptions = {},
): Promise<{ fileId: string; webViewLink: string; thumbnailLink?: string } | null> {
  const base64Response = await fetch(imageData);
  const blob = await base64Response.blob();

  try {
    const result = await uploadOneFile(
      new File([blob], fileName, { type: blob.type }),
      dealName,
      directories ?? ['Dokumente', 'Allgemeine Uploads'],
      destination,
    );
    if (!result?.fileId) return null;
    return {
      fileId: result.fileId,
      webViewLink: result.webViewLink || `https://drive.google.com/file/d/${result.fileId}`,
    };
  } catch (err: any) {
    console.error('[Drive] uploadSingleFile fehlgeschlagen:', err);
    throw new Error(`Drive Upload fehlgeschlagen: ${err.message}`);
  }
}

export async function getFileAsync(fileId: string): Promise<DriveFileContent | null> {
  try {
    const response = await one360.get<Blob>(
      `/api/v1/google/drive/file/${encodeURIComponent(fileId)}`,
      { responseType: 'blob' }
    );
    const blob = response as unknown as Blob;
    const base64Content = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { base64Content, mimeType: blob.type };
  } catch (err: any) {
    console.error('[Drive] getFile fehlgeschlagen:', err);
    return null;
  }
}

export async function deleteFileAsync(fileId: string): Promise<void> {
  try {
    await one360.delete(`/api/v1/google/drive/file/${encodeURIComponent(fileId)}`);
  } catch (err: any) {
    console.error('[Drive] deleteFile fehlgeschlagen:', err);
    throw new Error(`Drive Delete fehlgeschlagen: ${err.message}`);
  }
}

export function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [meta, base64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mime });
}
